import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/axios';
import { useAuth } from '../context/AuthContext';
import '../styles/style.css';

// Shown right after a user signs in with a temporary password set by the admin.
// They can't reach any other page until they pick their own password.
export default function SetPasswordPage() {
  const { admin, refreshUser, logout } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!form.currentPassword || !form.newPassword || !form.confirmPassword) {
      setError('Fill in all three fields.');
      return;
    }
    if (form.newPassword.length < 8) {
      setError('New password must be at least 8 characters.');
      return;
    }
    if (form.newPassword !== form.confirmPassword) {
      setError('New password and confirmation do not match.');
      return;
    }

    setBusy(true);
    try {
      await api.post('/auth/change-password', form);
      await refreshUser(); // mustChangePassword is now false
      navigate('/generate', { replace: true });
    } catch (err) {
      setError(err.response?.data?.message || 'Could not set your password. Try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="login-page">
      <div className="brand-panel">
        <div className="brand-glow" />
        <div className="brand-content">
          <div className="wordmark">HirePeaks</div>
          <div className="eyebrow">OFFER LETTER SYSTEM</div>
          <p className="tagline">
            Every offer, drafted and sent with the same care it took to earn it.
          </p>
        </div>
        <div className="brand-footer">Internal tool · Authorized staff only</div>
      </div>

      <div className="form-panel">
        <form className="login-card" onSubmit={handleSubmit} noValidate>
          <h1 className="login-title">Set your password</h1>
          <p className="login-subtitle">
            Hi {admin?.name?.split(' ')[0] || 'there'}, you signed in with a temporary password.
            Choose your own password to continue.
          </p>

          {error && (
            <div className="error-box" role="alert">
              <span>{error}</span>
            </div>
          )}

          <div className="field-group">
            <label className="field-label" htmlFor="sp-current">Temporary password</label>
            <input
              id="sp-current"
              className="hp-input"
              type={showPassword ? 'text' : 'password'}
              value={form.currentPassword}
              onChange={(e) => set('currentPassword', e.target.value)}
              autoComplete="current-password"
               placeholder="Enter Password"
              autoFocus
            />
          </div>

          <div className="field-group">
            <label className="field-label" htmlFor="sp-new">New password</label>
            <input
              id="sp-new"
              className="hp-input"
              type={showPassword ? 'text' : 'password'}
              value={form.newPassword}
              onChange={(e) => set('newPassword', e.target.value)}
              placeholder="At least 8 characters"
              autoComplete="new-password"
            />
          </div>

          <div className="field-group">
            <label className="field-label" htmlFor="sp-confirm">Confirm new password</label>
            <input
              id="sp-confirm"
              className="hp-input"
              type={showPassword ? 'text' : 'password'}
              value={form.confirmPassword}
              onChange={(e) => set('confirmPassword', e.target.value)}
              autoComplete="new-password"
               placeholder="Confirm New Password"
            />
          </div>

          {/* Inline styles override the global ".login-card input { width: 100% }" rule,
              which would otherwise stretch the checkbox across the whole row */}
          <label
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-start',
              gap: 8,
              fontSize: 13,
              color: '#334155',
              marginBottom: 16,
              cursor: 'pointer',
              userSelect: 'none',
            }}
          >
            <input
              type="checkbox"
              checked={showPassword}
              onChange={(e) => setShowPassword(e.target.checked)}
              style={{ width: 16, height: 16, padding: 0, margin: 0, flex: '0 0 auto', cursor: 'pointer' }}
            />
            <span>Show passwords</span>
          </label>

          <button type="submit" disabled={busy} className="hp-submit">
            {busy ? (
              <>
                <span className="hp-spinner" aria-hidden="true" />
                Saving…
              </>
            ) : (
              'Save password and continue'
            )}
          </button>

          <button
            type="button"
            className="back-to-login"
            onClick={logout}
            style={{ background: 'none', border: 'none', cursor: 'pointer', width: '100%' }}
          >
            Sign out
          </button>
        </form>
      </div>
    </div>
  );
}
